// src/schema/registry.ts
function registryKey(family, version) {
  return `${family}@v${version}`;
}
function deepEqual(a, b) {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }
  if (typeof a === "object") {
    const aObj = a;
    const bObj = b;
    const aKeys = Object.keys(aObj).sort();
    const bKeys = Object.keys(bObj).sort();
    if (aKeys.length !== bKeys.length) return false;
    if (!aKeys.every((k, i) => k === bKeys[i])) return false;
    return aKeys.every((k) => deepEqual(aObj[k], bObj[k]));
  }
  return false;
}
function schemasAreEqual(a, b) {
  return a.family === b.family && a.domain === b.domain && a.contextType === b.contextType && a.version === b.version && a.strictMode === b.strictMode && deepEqual(a.fields, b.fields);
}
var SchemaRegistry = class {
  /**
   * Primary map: registryKey → schema.
   * Key format: `{family}@v{version}`.
   */
  schemas = /* @__PURE__ */ new Map();
  /**
   * Fast latest-version table: family → highest registered version number.
   */
  latestVersions = /* @__PURE__ */ new Map();
  /**
   * Register a schema. Throws if a schema for the same family + version is
   * already registered with *different* field definitions (conflict). Re-
   * registering an identical schema is idempotent and succeeds silently.
   *
   * @throws {Error} When a conflicting schema is registered for the same key.
   */
  register(schema) {
    const key = registryKey(schema.family, schema.version);
    const existing = this.schemas.get(key);
    if (existing !== void 0) {
      if (!schemasAreEqual(existing, schema)) {
        throw new Error(
          `SchemaRegistry conflict: a different schema is already registered for "${key}". Deregister it first or use a new version number.`
        );
      }
      return;
    }
    this.schemas.set(key, schema);
    const currentLatest = this.latestVersions.get(schema.family);
    if (currentLatest === void 0 || schema.version > currentLatest) {
      this.latestVersions.set(schema.family, schema.version);
    }
  }
  /**
   * Retrieve a specific version of a schema, or `undefined` if not registered.
   */
  get(family, version) {
    return this.schemas.get(registryKey(family, version));
  }
  /**
   * Retrieve the schema at the highest registered version for a family, or
   * `undefined` if the family has never been registered.
   */
  getLatest(family) {
    const latestVersion = this.latestVersions.get(family);
    if (latestVersion === void 0) return void 0;
    return this.schemas.get(registryKey(family, latestVersion));
  }
  /**
   * Return all registered schemas in insertion order.
   */
  getAll() {
    return Array.from(this.schemas.values());
  }
  /**
   * Test whether a family (and optionally a specific version) is registered.
   *
   * @param family  The document family name.
   * @param version When supplied, checks whether that exact version is present.
   *                When omitted, checks whether any version of the family exists.
   */
  has(family, version) {
    if (version !== void 0) {
      return this.schemas.has(registryKey(family, version));
    }
    return this.latestVersions.has(family);
  }
  /**
   * Return all registered version numbers for a family, sorted ascending.
   * Returns an empty array if the family is unknown.
   */
  getAllVersions(family) {
    const versions = [];
    const prefix = `${family}@v`;
    for (const key of this.schemas.keys()) {
      if (key.startsWith(prefix)) {
        const versionStr = key.slice(prefix.length);
        const parsed = Number(versionStr);
        if (!Number.isNaN(parsed)) {
          versions.push(parsed);
        }
      }
    }
    return versions.sort((a, b) => a - b);
  }
  /**
   * Remove all registered schemas and version tracking state.
   * Primarily useful in tests.
   */
  clear() {
    this.schemas.clear();
    this.latestVersions.clear();
  }
};

// src/schema/validator.ts
function makeError(path, code, message) {
  return { path, code, message };
}
function validateField(value, field, path) {
  const errors = [];
  if (value === void 0 || value === null) {
    if (field.required === true) {
      errors.push(makeError(path, "REQUIRED", `Field "${path}" is required but was not provided.`));
    }
    return errors;
  }
  if (!isExpectedType(value, field.type)) {
    errors.push(
      makeError(
        path,
        "TYPE_MISMATCH",
        `Field "${path}" must be of type "${field.type}" but received "${runtimeType(value)}".`
      )
    );
    return errors;
  }
  if (field.enum !== void 0 && field.enum.length > 0) {
    if (!field.enum.includes(value)) {
      const allowed = field.enum.map((v) => JSON.stringify(v)).join(", ");
      errors.push(
        makeError(
          path,
          "ENUM_VIOLATION",
          `Field "${path}" must be one of [${allowed}] but received ${JSON.stringify(value)}.`
        )
      );
    }
  }
  if (field.type === "number") {
    const num = value;
    if (field.minimum !== void 0 && num < field.minimum) {
      errors.push(
        makeError(
          path,
          "MINIMUM_VIOLATION",
          `Field "${path}" must be \u2265 ${field.minimum} but received ${num}.`
        )
      );
    }
    if (field.maximum !== void 0 && num > field.maximum) {
      errors.push(
        makeError(
          path,
          "MAXIMUM_VIOLATION",
          `Field "${path}" must be \u2264 ${field.maximum} but received ${num}.`
        )
      );
    }
  }
  if (field.type === "string") {
    const str = value;
    if (field.minimum !== void 0 && str.length < field.minimum) {
      errors.push(
        makeError(
          path,
          "MIN_LENGTH_VIOLATION",
          `Field "${path}" must have at least ${field.minimum} character(s) but has ${str.length}.`
        )
      );
    }
    if (field.maximum !== void 0 && str.length > field.maximum) {
      errors.push(
        makeError(
          path,
          "MAX_LENGTH_VIOLATION",
          `Field "${path}" must have at most ${field.maximum} character(s) but has ${str.length}.`
        )
      );
    }
    if (field.pattern !== void 0) {
      const regex = new RegExp(field.pattern);
      if (!regex.test(str)) {
        errors.push(
          makeError(
            path,
            "PATTERN_VIOLATION",
            `Field "${path}" does not match the required pattern /${field.pattern}/.`
          )
        );
      }
    }
  }
  if (field.type === "array") {
    const arr = value;
    if (field.minimum !== void 0 && arr.length < field.minimum) {
      errors.push(
        makeError(
          path,
          "MIN_ITEMS_VIOLATION",
          `Field "${path}" must have at least ${field.minimum} item(s) but has ${arr.length}.`
        )
      );
    }
    if (field.maximum !== void 0 && arr.length > field.maximum) {
      errors.push(
        makeError(
          path,
          "MAX_ITEMS_VIOLATION",
          `Field "${path}" must have at most ${field.maximum} item(s) but has ${arr.length}.`
        )
      );
    }
    if (field.items !== void 0) {
      arr.forEach((item, index) => {
        const itemPath = `${path}[${index}]`;
        errors.push(...validateField(item, field.items, itemPath));
      });
    }
  }
  if (field.type === "object" && field.properties !== void 0) {
    const obj = value;
    errors.push(...validateObject(obj, field.properties, path, true));
  }
  return errors;
}
function validateObject(data, fields, basePath, strictMode) {
  const errors = [];
  const isStrict = strictMode !== false;
  if (isStrict) {
    for (const key of Object.keys(data)) {
      if (!(key in fields)) {
        const path = basePath === "" ? key : `${basePath}.${key}`;
        errors.push(
          makeError(
            path,
            "UNKNOWN_KEY",
            `Unknown field "${path}" is not permitted in strict mode.`
          )
        );
      }
    }
  }
  for (const [fieldName, fieldDef] of Object.entries(fields)) {
    const path = basePath === "" ? fieldName : `${basePath}.${fieldName}`;
    const value = data[fieldName];
    errors.push(...validateField(value, fieldDef, path));
  }
  return errors;
}
function validateDocument(data, schema) {
  const errors = validateObject(data, schema.fields, "", schema.strictMode);
  return {
    valid: errors.length === 0,
    errors
  };
}
function isExpectedType(value, expected) {
  switch (expected) {
    case "string":
      return typeof value === "string";
    case "number":
      return typeof value === "number" && !Number.isNaN(value);
    case "boolean":
      return typeof value === "boolean";
    case "array":
      return Array.isArray(value);
    case "object":
      return typeof value === "object" && value !== null && !Array.isArray(value);
  }
}
function runtimeType(value) {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
}

export {
  SchemaRegistry,
  validateField,
  validateDocument
};
//# sourceMappingURL=chunk-OZTJOR6R.js.map